/**
 * Reclaim · Daily Essentials — Phase 1
 *
 * The 'sun' counterpart to Biohacking Sleep (the 'moon'). This is the
 * member's daily rhythm — Morning Gratitude · Activation · Routine ·
 * Intention · Daily Audio · Resets · Evening Reflection. Calm, clean,
 * essential. Like brushing your teeth for the mind, body and nervous
 * system. Route: /daily-essentials
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { haptic } from "../../lib/haptics";
import {
  ArrowLeft, Sun, Sunrise, Sunset, Check, ChevronRight, Loader2,
  Sparkles, Headphones, BookOpen, RefreshCw, Heart, Music2, X,
  Play, Pause, Share2, Flame,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` });

// Card icon per item key — adds visual rhythm.
const ICON = {
  morning_gratitude: Heart,
  morning_activation: Sunrise,
  full_morning_routine: Sun,
  daily_immersion: Flame,
  morning_intention: Sparkles,
  daily_audio: Music2,
  daily_resets: RefreshCw,
  evening_reflection: BookOpen,
};

export default function DailyEssentialsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [week, setWeek] = useState(null);
  const [activation, setActivation] = useState(null); // info from /morning-activation/info
  const [loading, setLoading] = useState(true);
  const [openItem, setOpenItem] = useState(null); // key of card opened in modal
  // Inline audio player state (Morning Activation Express).
  const audioRef = useRef(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0); // 0..1

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const headers = auth();
      // `cache: "no-store"` is critical here — without it, iOS Safari /
      // intermediate proxies sometimes serve a stale /week response right
      // after marking an item done, leaving the Rhythm Map a beat behind
      // the card's ✓ tick.
      const [todayR, weekR, activR] = await Promise.all([
        fetch(`${API}/api/daily-essentials/today`, { headers, cache: "no-store" }),
        fetch(`${API}/api/daily-essentials/week`, { headers, cache: "no-store" }),
        fetch(`${API}/api/daily-essentials/morning-activation/info`, { headers, cache: "no-store" }),
      ]);
      if (todayR.ok) setData(await todayR.json());
      if (weekR.ok) setWeek(await weekR.json());
      if (activR.ok) setActivation(await activR.json());
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refresh ONLY the rhythm-map week view — used right after a mutation
  // succeeds so the Tue column lights up without showing the full-page
  // skeleton (load() flashes the spinner because it flips setLoading).
  // Accepts an `expectKey` — if the response doesn't reflect the just-
  // marked item on today, we retry once (defeats read-your-writes races).
  const refreshWeek = useCallback(async (expectKey = null) => {
    const bust = Date.now();
    try {
      const r = await fetch(
        `${API}/api/daily-essentials/week?_t=${bust}`,
        { headers: auth(), cache: "no-store" }
      );
      if (!r.ok) return;
      const w = await r.json();
      // Sanity check: does today reflect the write? If not, retry once.
      if (expectKey && Array.isArray(w?.days)) {
        const today = w.days.find((d) => d.is_today);
        const seen = today && today.completed?.includes(expectKey);
        if (!seen) {
          await new Promise((res) => setTimeout(res, 350));
          const r2 = await fetch(
            `${API}/api/daily-essentials/week?_t=${Date.now()}`,
            { headers: auth(), cache: "no-store" }
          );
          if (r2.ok) { setWeek(await r2.json()); return; }
        }
      }
      setWeek(w);
    } catch (_) { /* ignore */ }
  }, []);

  const completeItem = async (key) => {
    // Haptic pulse — tactile confirmation before pixels move.
    haptic("tick");
    // Optimistic update — tick the today cell in Rhythm Map *immediately*.
    // The subsequent server refresh confirms; on failure we roll back.
    setWeek((prev) => {
      if (!prev?.days) return prev;
      return {
        ...prev,
        days: prev.days.map((d) =>
          d.is_today && !d.completed?.includes(key)
            ? { ...d, completed: [...(d.completed || []), key] }
            : d
        ),
        totals: { ...(prev.totals || {}), [key]: (prev.totals?.[key] || 0) + 1 },
      };
    });
    try {
      // The POST returns the full updated today-payload, so we can
      // mark the card ✓ immediately without waiting for load().
      const r = await fetch(`${API}/api/daily-essentials/complete`, {
        method: "POST", headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({ key }), cache: "no-store",
      });
      if (r.ok) setData(await r.json());
      refreshWeek(key);
    } catch (_) { load(); }
  };

  // Morning Activation inline player ────────────────────────────
  const toggleActivation = () => {
    if (!audioRef.current) return;
    if (audioPlaying) {
      audioRef.current.pause();
      setAudioPlaying(false);
    } else {
      audioRef.current.play().then(() => setAudioPlaying(true)).catch(() => {});
    }
  };
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setAudioProgress(a.duration ? a.currentTime / a.duration : 0);
    const onEnd = () => {
      setAudioPlaying(false);
      setAudioProgress(1);
      // Auto-tick the card when the audio completes naturally.
      completeItem("morning_activation");
    };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("ended", onEnd);
    };
  }, [activation]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0B0805] via-[#0F0B07] to-[#080604] text-white">
      <header className="px-5 pt-5 pb-3 flex items-center gap-3 sticky top-0 backdrop-blur bg-[#0B0805]/85 z-10">
        {/* Back is handled globally by <GlobalBackButton/> — single
            source of truth for navigation. Header here just holds
            the section title. */}
        <div className="flex items-center gap-2 ml-12">
          <Sun size={20} className="text-amber-300" />
          <h1 className="text-xl tracking-wide" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
            Daily Essentials
          </h1>
        </div>
      </header>

      <main className="px-5 pb-24 max-w-2xl mx-auto">
        {loading || !data ? (
          <div className="flex items-center justify-center py-32 text-white/45">
            <Loader2 size={20} className="animate-spin" />
          </div>
        ) : (
          <>
            <ScoreCard score={data.score} />

            {week && <RhythmMap week={week} />}

            <div className="mt-6 space-y-3" data-testid="daily-essentials-list">
              {data.items.map((item) => (
                <EssentialCard
                  key={item.key}
                  item={item}
                  data={data}
                  activation={activation}
                  audioPlaying={audioPlaying}
                  audioProgress={audioProgress}
                  onToggleActivation={toggleActivation}
                  onOpen={() => setOpenItem(item.key)}
                  onComplete={() => completeItem(item.key)}
                  onNavigate={(path) => navigate(path)}
                />
              ))}
            </div>

            {/* Single hidden audio element — Morning Activation Express. */}
            {activation?.ready && (
              <audio
                ref={audioRef}
                src={`${API}${activation.audio_url}`}
                preload="metadata"
                data-testid="morning-activation-audio"
              />
            )}

            {/* Today's tally — warm, calm, tier-phrased. Mirrors the
                Sleep Checklist footer so the consistency-over-perfection
                ethos feels unified across both surfaces. */}
            <TodayTally score={data.score} />
          </>
        )}
      </main>

      {/* Modals — pass a `setToday` so the modal can update state
          from the POST response immediately, and `refreshWeek` so the
          Rhythm Map's Tue column lights up without a full reload. */}
      {openItem === "morning_gratitude" && (
        <GratitudeModal initial={data?.morning_gratitude} onClose={() => setOpenItem(null)} onSaved={(payload) => { if (payload) setData(payload); refreshWeek(); setOpenItem(null); }} />
      )}
      {openItem === "morning_intention" && (
        <IntentionModal initial={data?.morning_intention} qualities={data?.intention_qualities || []} onClose={() => setOpenItem(null)} onSaved={(payload) => { if (payload) setData(payload); refreshWeek(); setOpenItem(null); }} />
      )}
      {openItem === "evening_reflection" && (
        <ReflectionModal initial={data?.evening_reflection} onClose={() => setOpenItem(null)} onSaved={(payload) => { if (payload) setData(payload); refreshWeek(); setOpenItem(null); }} />
      )}
      {openItem === "full_morning_routine" && (
        <FullMorningRoutineVideoModal
          videoUrl={data?.items?.find((i) => i.key === "full_morning_routine")?.video_url}
          onClose={() => setOpenItem(null)}
          onComplete={() => { completeItem("full_morning_routine"); setOpenItem(null); }}
        />
      )}
    </div>
  );
}

// ── Today's tally · warm encouragement footer ───────────────
// Same tone-system as Sleep's TonightsTally. Calm phrasing scales
// with how much rhythm has been kept today. Never grade-language.
function TodayTally({ score }) {
  const done = score?.done ?? 0;
  const total = score?.total ?? 8;
  let tone;
  if (total === 0)                  tone = "A fresh canvas.";
  else if (done === 0)              tone = "The objective is rhythm, not perfection.";
  else if (done <= total * 0.25)    tone = "A gentle start.";
  else if (done <= total * 0.55)    tone = "Finding your rhythm.";
  else if (done <= total * 0.80)    tone = "Beautiful flow today.";
  else if (done < total)            tone = "Almost the whole rhythm.";
  else                              tone = "Every essential met. Beautiful day.";
  return (
    <div
      data-testid="daily-todays-tally"
      className="mt-8 rounded-xl bg-gradient-to-br from-amber-500/8 via-emerald-500/4 to-transparent ring-1 ring-amber-300/15 p-4 text-center"
    >
      <div className="text-amber-200/55 text-[10px] uppercase tracking-[0.28em]">Today</div>
      <div className="mt-1 text-2xl font-light text-amber-50/95" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
        {done} of {total} in rhythm
      </div>
      <p className="mt-1 text-white/55 text-xs italic" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
        {tone}
      </p>
    </div>
  );
}

// ── Score card (top of page) ────────────────────────────────────
function ScoreCard({ score }) {
  const pct = score?.percent ?? 0;
  return (
    <div
      className="rounded-2xl bg-gradient-to-br from-amber-500/15 via-yellow-500/8 to-transparent ring-1 ring-amber-400/25 p-6"
      data-testid="daily-essentials-score-card"
    >
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-amber-200/70 text-[10px] uppercase tracking-[0.3em]">Today&apos;s Rhythm</div>
          <div className="mt-1 text-4xl font-light text-amber-50/95" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
            {score?.done ?? 0} <span className="text-white/35 text-2xl">/ {score?.total ?? 7}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-amber-200 text-3xl font-light">{pct}%</div>
          <div className="text-white/40 text-[10px] mt-0.5">complete</div>
        </div>
      </div>
      <div className="mt-4 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-amber-300 to-amber-500 transition-all duration-700"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ── Rhythm Map (7-day × 7-habit grid) ───────────────────────────
// Calm, GitHub-style contribution grid. Each row = one habit, each
// column = one day. Filled gold cells = the habit was hit that day.
// Reinforces consistency over perfection.
function RhythmMap({ week }) {
  const days = week.days || [];
  const items = week.items || [];
  const totals = week.totals || {};
  return (
    <div className="mt-5 rounded-2xl bg-white/[0.02] ring-1 ring-white/8 p-5" data-testid="daily-rhythm-map">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-amber-200/70 text-[10px] uppercase tracking-[0.3em]">Rhythm Map</div>
          <div className="text-white/60 text-[11px] italic mt-0.5">Last 7 days · consistency, not perfection.</div>
        </div>
      </div>
      <div className="overflow-x-auto -mx-1 px-1">
        <table className="w-full text-[10px] text-white/55 select-none" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
          <thead>
            <tr>
              <th className="text-left font-normal pr-3 align-bottom"></th>
              {days.map((d) => (
                <th key={d.date} className={`font-normal pb-1 text-center ${d.is_today ? "text-amber-200" : "text-white/45"}`}>
                  {d.weekday}
                </th>
              ))}
              <th className="font-normal pl-3 text-right text-white/45 align-bottom">×7</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.key}>
                <td className="py-1 pr-3 truncate max-w-[7.5rem] text-white/70 text-[11px]" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
                  {it.label}
                </td>
                {days.map((d) => {
                  const done = (d.completed || []).includes(it.key);
                  const isTodayCell = d.is_today;
                  return (
                    <td key={d.date} className="py-1 text-center">
                      <motion.div
                        title={`${it.label} · ${d.weekday}`}
                        data-testid={`rhythm-cell-${it.key}-${d.date}`}
                        initial={false}
                        animate={
                          done && isTodayCell
                            ? {
                                scale: [1, 1.35, 1],
                                boxShadow: [
                                  "0 0 0px 0px rgba(245, 158, 11, 0)",
                                  "0 0 12px 3px rgba(245, 158, 11, 0.55)",
                                  "0 0 0px 0px rgba(245, 158, 11, 0)",
                                ],
                              }
                            : { scale: 1, boxShadow: "0 0 0px 0px rgba(0,0,0,0)" }
                        }
                        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                        className={`mx-auto w-4 h-4 rounded-sm ${
                          done
                            ? "bg-amber-400/90 ring-1 ring-amber-300/50"
                            : "bg-white/[0.04] ring-1 ring-white/8"
                        } ${isTodayCell ? "outline outline-1 outline-amber-300/40 outline-offset-1" : ""}`}
                      />
                    </td>
                  );
                })}
                <td className="py-1 pl-3 text-right text-white/55">{totals[it.key] || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Individual card ─────────────────────────────────────────────
function EssentialCard({ item, data, activation, audioPlaying, audioProgress, onToggleActivation, onOpen, onComplete, onNavigate }) {
  const Icon = ICON[item.key] || Sparkles;
  const isDone = !!item.completed;
  const isFlagship = item.key === "daily_immersion";

  // Morning Activation has its own special play UI — render that
  // dedicated card and short-circuit.
  if (item.key === "morning_activation") {
    return (
      <MorningActivationCard
        item={item}
        isDone={isDone}
        activation={activation}
        playing={audioPlaying}
        progress={audioProgress}
        onToggle={onToggleActivation}
        onComplete={onComplete}
      />
    );
  }

  // What does the card's primary button do?
  const handleAction = () => {
    if (item.kind === "prompt") {
      // Open the inline modal for gratitude / intention / reflection.
      onOpen();
    } else if (item.kind === "video") {
      // Full Morning Routine — play the Day-2 "YES" Part-2 video in a
      // full-screen modal. Completion is fired when the video ends
      // (handled inside the modal) so tapping doesn't tick early.
      onOpen();
    } else if (item.kind === "audio") {
      // Legacy audio card — tap = mark complete.
      onComplete();
    } else if (item.kind === "link" && item.deep_link) {
      // Mark complete *and* navigate — for Resets / Daily Audio.
      onComplete();
      setTimeout(() => onNavigate(item.deep_link), 60);
    }
  };

  // Saved summary line (e.g. show today's chosen quality).
  let savedSummary = null;
  if (isDone) {
    if (item.key === "morning_gratitude" && data?.morning_gratitude?.appreciations?.length) {
      savedSummary = `"${data.morning_gratitude.appreciations[0]}"…`;
    } else if (item.key === "morning_intention" && data?.morning_intention?.quality) {
      const q = (data.intention_qualities || []).find((x) => x.id === data.morning_intention.quality);
      savedSummary = q ? `Today: ${q.label}` : null;
    } else if (item.key === "evening_reflection" && data?.evening_reflection?.went_well) {
      savedSummary = `"${data.evening_reflection.went_well}"`;
    }
  }

  // The Daily Immersion card is wrapped in a soft halo + eyebrow to
  // mark it as the flagship "universal habit". Visually calm — the
  // halo is amber/8% alpha, the eyebrow is small uppercase tracked.
  // Subliminal cue: this is the cornerstone, without shouting.
  const cardButton = (
    <button
      onClick={handleAction}
      data-testid={`daily-card-${item.key}`}
      className={`relative w-full text-left rounded-2xl ring-1 transition flex items-start gap-4 ${
        isFlagship ? "p-5" : "p-4"
      } ${
        isFlagship
          ? isDone
            ? "bg-gradient-to-br from-amber-500/[0.10] via-amber-400/[0.05] to-transparent ring-amber-300/35 shadow-[0_0_32px_-12px_rgba(245,158,11,0.30)] hover:ring-amber-200/55"
            : "bg-gradient-to-br from-amber-500/[0.08] via-amber-400/[0.04] to-transparent ring-amber-300/30 shadow-[0_0_32px_-14px_rgba(245,158,11,0.22)] hover:ring-amber-200/45"
          : isDone
            ? "bg-amber-500/[0.06] ring-amber-400/20 hover:ring-amber-300/35"
            : "bg-white/[0.025] ring-white/8 hover:ring-white/20 hover:bg-white/[0.04]"
      }`}
    >
      <div className={`rounded-xl flex items-center justify-center flex-shrink-0 ${
        isFlagship ? "w-12 h-12" : "w-11 h-11"
      } ${
        isFlagship
          ? "bg-amber-400/25 text-amber-100 ring-1 ring-amber-300/30"
          : isDone
            ? "bg-amber-400/20 text-amber-200"
            : "bg-white/[0.04] text-white/55"
      }`}>
        <Icon size={isFlagship ? 22 : 20} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className={`font-light ${isFlagship ? "text-lg" : "text-base"} ${
            isFlagship
              ? "text-amber-50"
              : isDone ? "text-amber-50/90" : "text-white/90"
          }`} style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
            {item.label}
          </h3>
          {item.subtitle && (
            <span className="text-[10px] uppercase tracking-wider text-white/40 px-1.5 py-0.5 rounded ring-1 ring-white/10 bg-white/[0.02]">
              {item.subtitle}
            </span>
          )}
        </div>
        <p className={`text-xs mt-1 leading-relaxed ${isFlagship ? "text-amber-100/65" : "text-white/55"}`}>
          {item.tagline}
        </p>
        {savedSummary && (
          <p className="text-amber-200/70 text-[11px] italic mt-1.5 truncate" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
            {savedSummary}
          </p>
        )}
        {item.kind === "audio" && item.key === "full_morning_routine" && !isDone && (
          <p className="text-white/35 text-[10px] mt-1.5 italic">Audio coming soon — Michael is recording.</p>
        )}
        {item.kind === "video" && item.key === "full_morning_routine" && !isDone && (
          <p className="text-amber-200/70 text-[10px] mt-1.5 italic">Tap to play · Part 2 · The Full Morning Routine</p>
        )}
      </div>
      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
        {isDone ? (
          <div className={`rounded-full bg-amber-400/90 flex items-center justify-center ${isFlagship ? "w-8 h-8" : "w-7 h-7"}`} data-testid={`daily-tick-${item.key}`}>
            <Check size={isFlagship ? 17 : 15} className="text-[#0B0805]" strokeWidth={3} />
          </div>
        ) : (
          <ChevronRight size={isFlagship ? 20 : 18} className={`mt-1 ${isFlagship ? "text-amber-200/60" : "text-white/30"}`} />
        )}
      </div>
    </button>
  );

  if (!isFlagship) return cardButton;

  // Wrap the flagship card with a small "Universal Habit" eyebrow.
  return (
    <div className="relative" data-testid="flagship-universal-habit">
      <div className="flex items-center gap-2 px-1 mb-1.5">
        <span className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-300/30 to-transparent" />
        <span className="text-[9px] uppercase tracking-[0.32em] text-amber-200/75 flex items-center gap-1">
          <Flame size={9} />
          The Universal Habit
        </span>
        <span className="h-px flex-1 bg-gradient-to-l from-transparent via-amber-300/30 to-transparent" />
      </div>
      {cardButton}
    </div>
  );
}

// ── Morning Activation card with inline player ──────────────────
function MorningActivationCard({ item, isDone, activation, playing, progress, onToggle, onComplete }) {
  const Icon = ICON[item.key] || Sunrise;
  const ready = !!activation?.ready;
  const totalSec = activation?.duration_seconds || 180;
  const elapsedSec = Math.floor(progress * totalSec);
  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  const [shareNote, setShareNote] = useState(null);

  // Build the public, unauthenticated share URL using the current
  // window origin so it works in every environment (preview + prod).
  const shareUrl = (typeof window !== "undefined")
    ? `${window.location.origin}/share/morning-activation`
    : "/share/morning-activation";

  const handleShare = async (e) => {
    e?.stopPropagation?.();
    const payload = {
      title: "Morning Activation · Reclaim",
      text: "Three minutes to begin the day consciously — start with me.",
      url: shareUrl,
    };
    try {
      if (navigator.share) {
        await navigator.share(payload);
        setShareNote("Shared");
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setShareNote("Link copied");
      }
    } catch (_) {
      // User cancelled OR clipboard blocked — fall back gracefully.
      try {
        await navigator.clipboard.writeText(shareUrl);
        setShareNote("Link copied");
      } catch (__) {
        setShareNote("Copy failed — long-press the link");
      }
    }
    setTimeout(() => setShareNote(null), 2200);
  };

  return (
    <div
      data-testid="daily-card-morning_activation"
      className={`w-full rounded-2xl ring-1 transition p-4 ${
        isDone
          ? "bg-amber-500/[0.06] ring-amber-400/20"
          : "bg-white/[0.025] ring-white/8 hover:ring-white/15"
      }`}
    >
      <div className="flex items-start gap-4">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
          isDone ? "bg-amber-400/20 text-amber-200" : "bg-white/[0.04] text-white/55"
        }`}>
          <Icon size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className={`text-base font-light ${isDone ? "text-amber-50/90" : "text-white/90"}`} style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
              {item.label}
            </h3>
            {item.subtitle && (
              <span className="text-[10px] uppercase tracking-wider text-white/40 px-1.5 py-0.5 rounded ring-1 ring-white/10 bg-white/[0.02]">
                {item.subtitle}
              </span>
            )}
            {!ready && (
              <span className="text-[10px] uppercase tracking-wider text-amber-200/60 px-1.5 py-0.5 rounded ring-1 ring-amber-300/15 bg-amber-400/[0.04]">
                preparing
              </span>
            )}
          </div>
          <p className="text-white/55 text-xs mt-1 leading-relaxed">
            {item.tagline}
          </p>
        </div>
        {isDone && (
          <div className="w-7 h-7 rounded-full bg-amber-400/90 flex items-center justify-center" data-testid="daily-tick-morning_activation">
            <Check size={15} className="text-[#0B0805]" strokeWidth={3} />
          </div>
        )}
      </div>

      {/* Inline player row */}
      <div className="mt-3 flex items-center gap-3">
        <button
          onClick={onToggle}
          disabled={!ready}
          data-testid="morning-activation-play"
          className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 transition ${
            ready
              ? playing
                ? "bg-amber-300 text-[#0B0805]"
                : "bg-amber-400/90 text-[#0B0805] hover:bg-amber-300"
              : "bg-white/[0.05] text-white/30 cursor-not-allowed"
          }`}
        >
          {playing ? <Pause size={16} /> : <Play size={16} className="translate-x-[1px]" />}
        </button>
        <div className="flex-1">
          <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-amber-300 to-amber-500 transition-all"
              style={{ width: `${Math.min(100, progress * 100)}%` }}
              data-testid="morning-activation-progress"
            />
          </div>
          <div className="flex items-center justify-between mt-1 text-[10px] text-white/45 tabular-nums">
            <span>{fmt(elapsedSec)}</span>
            <span>{fmt(Math.round(totalSec))}</span>
          </div>
        </div>
        {!isDone && (
          <button
            onClick={onComplete}
            data-testid="morning-activation-mark-done"
            className="text-[10px] uppercase tracking-wider text-white/45 hover:text-amber-200 transition px-2"
            title="Mark complete without listening"
          >
            Mark done
          </button>
        )}
      </div>

      {/* Share row — appears once the audio is ready */}
      {ready && (
        <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between gap-3">
          <p className="text-white/45 text-[11px] italic flex-1 min-w-0 truncate">
            Send this audio to a friend — let them start their day with you.
          </p>
          <button
            onClick={handleShare}
            data-testid="morning-activation-share"
            className="inline-flex items-center gap-1.5 text-[11px] text-amber-200/85 hover:text-amber-100 px-3 py-1.5 rounded-full ring-1 ring-amber-300/25 hover:ring-amber-200/40 transition flex-shrink-0"
          >
            <Share2 size={12} />
            {shareNote || "Share"}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Morning Gratitude modal ─────────────────────────────────────
function GratitudeModal({ initial, onClose, onSaved }) {
  const init = initial?.appreciations || ["", "", ""];
  const [lines, setLines] = useState([init[0] || "", init[1] || "", init[2] || ""]);
  const [lookingForward, setLookingForward] = useState(initial?.looking_forward || "");
  const [saving, setSaving] = useState(false);

  const update = (i, v) => setLines((arr) => arr.map((x, idx) => (idx === i ? v : x)));

  const canSave = lines.some((l) => l.trim().length > 0);

  const submit = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/daily-essentials/morning-gratitude`, {
        method: "POST", headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({
          appreciations: lines.filter((l) => l.trim()),
          looking_forward: lookingForward,
        }),
        cache: "no-store",
      });
      const payload = r.ok ? await r.json() : null;
      onSaved(payload);
    } catch (_) { onSaved(null); } finally { setSaving(false); }
  };

  return (
    <ModalShell title="Morning Gratitude" subtitle="Before reaching for the world, reconnect with yourself." onClose={onClose}>
      <p className="text-amber-200/75 text-xs uppercase tracking-[0.22em] mt-1">Three things I appreciate today</p>
      <div className="mt-3 space-y-2">
        {[0, 1, 2].map((i) => (
          <input
            key={i}
            value={lines[i]}
            onChange={(e) => update(i, e.target.value)}
            placeholder={i === 0 ? "Something simple…" : i === 1 ? "Someone…" : "Anything…"}
            data-testid={`gratitude-line-${i}`}
            className="w-full bg-white/[0.04] ring-1 ring-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white/95 focus:outline-none focus:ring-amber-400/40 placeholder-white/30"
            autoFocus={i === 0}
          />
        ))}
      </div>
      <p className="text-amber-200/75 text-xs uppercase tracking-[0.22em] mt-5">One thing I am looking forward to</p>
      <input
        value={lookingForward}
        onChange={(e) => setLookingForward(e.target.value)}
        placeholder="Today I'm looking forward to…"
        data-testid="gratitude-looking-forward"
        className="mt-3 w-full bg-white/[0.04] ring-1 ring-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white/95 focus:outline-none focus:ring-amber-400/40 placeholder-white/30"
      />
      <ModalActions onCancel={onClose} onSave={submit} disabled={!canSave} saving={saving} testIdPrefix="gratitude" />
    </ModalShell>
  );
}

// ── Morning Intention modal ─────────────────────────────────────
function IntentionModal({ initial, qualities, onClose, onSaved }) {
  const [selected, setSelected] = useState(initial?.quality || null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!selected || saving) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/daily-essentials/morning-intention`, {
        method: "POST", headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({ quality: selected }),
        cache: "no-store",
      });
      const payload = r.ok ? await r.json() : null;
      onSaved(payload);
    } catch (_) { onSaved(null); } finally { setSaving(false); }
  };

  return (
    <ModalShell title="Morning Intention" subtitle="What quality would you like to embody today?" onClose={onClose}>
      <div className="mt-2 grid grid-cols-2 gap-2" data-testid="intention-grid">
        {qualities.map((q) => {
          const active = selected === q.id;
          return (
            <button
              key={q.id}
              onClick={() => setSelected(q.id)}
              data-testid={`intention-quality-${q.id}`}
              className={`py-3 rounded-lg text-sm transition ring-1 ${
                active
                  ? "bg-amber-400/25 ring-amber-400/55 text-amber-50"
                  : "bg-white/[0.03] ring-white/8 text-white/75 hover:bg-white/[0.06]"
              }`}
            >
              {q.label}
            </button>
          );
        })}
      </div>
      <ModalActions
        onCancel={onClose}
        onSave={submit}
        disabled={!selected}
        saving={saving}
        saveLabel={selected ? `Set ${qualities.find((q) => q.id === selected)?.label}` : "Pick one"}
        testIdPrefix="intention"
      />
    </ModalShell>
  );
}

// ── Evening Reflection modal ────────────────────────────────────
function ReflectionModal({ initial, onClose, onSaved }) {
  const [wentWell, setWentWell] = useState(initial?.went_well || "");
  const [learned, setLearned]     = useState(initial?.learned   || "");
  const [grateful, setGrateful]   = useState(initial?.grateful  || "");
  const [saving, setSaving] = useState(false);

  const canSave = [wentWell, learned, grateful].some((v) => v.trim().length > 0);

  const submit = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/daily-essentials/evening-reflection`, {
        method: "POST", headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({ went_well: wentWell, learned, grateful }),
        cache: "no-store",
      });
      const payload = r.ok ? await r.json() : null;
      onSaved(payload);
    } catch (_) { onSaved(null); } finally { setSaving(false); }
  };

  return (
    <ModalShell title="Evening Reflection" subtitle="Close the day with three small truths." onClose={onClose}>
      <ReflectField label="What went well today?" value={wentWell} onChange={setWentWell} placeholder="A win, a moment, a small thing…" testid="reflect-went-well" autoFocus />
      <ReflectField label="What did I learn today?" value={learned} onChange={setLearned} placeholder="One insight…" testid="reflect-learned" />
      <ReflectField label="What am I grateful for?" value={grateful} onChange={setGrateful} placeholder="Anything at all…" testid="reflect-grateful" />
      <ModalActions onCancel={onClose} onSave={submit} disabled={!canSave} saving={saving} testIdPrefix="reflection" />
    </ModalShell>
  );
}

function ReflectField({ label, value, onChange, placeholder, testid, autoFocus }) {
  return (
    <div className="mt-4">
      <p className="text-amber-200/75 text-xs uppercase tracking-[0.22em]">{label}</p>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={2}
        autoFocus={autoFocus}
        data-testid={testid}
        className="mt-2 w-full bg-white/[0.04] ring-1 ring-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white/95 focus:outline-none focus:ring-amber-400/40 placeholder-white/30 resize-none"
      />
    </div>
  );
}

// ── Shared modal chrome ─────────────────────────────────────────
function ModalShell({ title, subtitle, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-5" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        // Use dvh (dynamic viewport) so iOS Safari shrinks the modal
        // when the on-screen keyboard opens — otherwise the Save button
        // gets hidden behind the keyboard / home-indicator. Add safe-area
        // padding on the bottom edge so the action row always clears the
        // tab bar + iOS gesture handle.
        className="w-full max-w-md rounded-t-3xl sm:rounded-3xl bg-[#0B0805] ring-1 ring-amber-400/20 px-6 pt-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] max-h-[88dvh] overflow-y-auto overscroll-contain"
        data-testid="daily-modal"
      >
        <div className="flex items-start justify-between mb-1">
          <h3 className="text-2xl text-amber-50/95 font-light leading-tight" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
            {title}
          </h3>
          <button onClick={onClose} className="text-white/40 hover:text-white/80 p-1 -mr-1" data-testid="daily-modal-close">
            <X size={18} />
          </button>
        </div>
        <p className="text-white/45 text-xs italic" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>{subtitle}</p>
        {children}
      </div>
    </div>
  );
}

function ModalActions({ onCancel, onSave, disabled, saving, saveLabel = "Save", testIdPrefix }) {
  return (
    <div className="flex items-center gap-2 mt-6">
      <button onClick={onCancel} className="flex-1 py-2.5 rounded-lg ring-1 ring-white/15 text-white/65 text-sm hover:bg-white/[0.04]" data-testid={`${testIdPrefix}-cancel`}>
        Cancel
      </button>
      <button
        onClick={onSave}
        disabled={disabled || saving}
        data-testid={`${testIdPrefix}-save`}
        className="flex-1 py-2.5 rounded-lg bg-amber-400/90 hover:bg-amber-400 text-[#0B0805] text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {saving ? "Saving…" : saveLabel}
      </button>
    </div>
  );
}


// ── Full Morning Routine · video modal ──────────────────────────
// Plays the Day-2 "YES" Part-2 video from the Integration Program.
// Auto-ticks the card when the video ends naturally (mirrors the
// Morning Activation audio behaviour).
function FullMorningRoutineVideoModal({ videoUrl, onClose, onComplete }) {
  const vidRef = useRef(null);
  const src = videoUrl ? `${API}${videoUrl}` : null;
  const stopAndClose = () => {
    try { vidRef.current && vidRef.current.pause(); } catch (_) { /* noop */ }
    onClose();
  };
  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex flex-col"
      data-testid="full-morning-routine-video-modal"
    >
      <div className="flex items-center justify-between px-5 py-4">
        <div className="flex items-center gap-2">
          <Sun size={16} className="text-amber-300" />
          <span
            className="text-amber-100/90 text-sm tracking-wide"
            style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
          >
            Full Morning Routine · Part 2
          </span>
        </div>
        <button
          onClick={stopAndClose}
          className="text-white/60 hover:text-white p-1"
          data-testid="full-morning-routine-close"
          aria-label="Close"
        >
          <X size={20} />
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center px-3 pb-6">
        {src ? (
          <video
            ref={vidRef}
            src={src}
            controls
            autoPlay
            playsInline
            className="w-full max-w-[900px] max-h-full rounded-xl bg-black"
            onEnded={onComplete}
            data-testid="full-morning-routine-video"
          />
        ) : (
          <p className="text-white/60 text-sm italic">Video is loading — please close and reopen shortly.</p>
        )}
      </div>
    </div>
  );
}
